import type { TFunction } from 'i18next';
import type { NotificationView } from '../api/generated/models/NotificationView';

const TASK_STATUS_KEYS = new Set(['open', 'in_progress', 'done', 'dismissed']);

export function taskStatusLabel(status: string | undefined, t: TFunction): string {
  if (!status || !TASK_STATUS_KEYS.has(status)) {
    return status ?? '';
  }
  return t(`tasks_status_${status}`);
}

export function formatNotificationTime(iso: string | undefined, t: TFunction): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) {
    return t('notifications.time.just_now');
  }
  if (diffMin < 60) {
    return t('notifications.time.minutes_ago', { count: diffMin });
  }
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24 && now.getDate() === date.getDate()) {
    return t('notifications.time.hours_ago', { count: diffHours });
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86_400_000);

  if (dayDiff === 0) {
    return t('notifications.time.today');
  }
  if (dayDiff === 1) {
    return t('notifications.time.yesterday');
  }
  if (dayDiff > 1 && dayDiff < 7) {
    return t('notifications.time.days_ago', { count: dayDiff });
  }

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function notificationRowTitle(item: NotificationView, t: TFunction): string {
  if (item.type === 'task_assigned') {
    return t('notifications.feed.task_assigned');
  }
  if (item.type === 'task_changed') {
    return t('notifications.feed.task_changed');
  }
  return t('notifications.feed.generic');
}

export function notificationRowBody(item: NotificationView, t: TFunction): string {
  const data = item.data ?? {};
  const taskTitle = typeof data.taskTitle === 'string' ? data.taskTitle : '';
  const changeCount = typeof data.changeCount === 'number' ? data.changeCount : 0;
  const statusFrom = typeof data.statusFrom === 'string' ? data.statusFrom : undefined;
  const statusTo = typeof data.statusTo === 'string' ? data.statusTo : undefined;

  if (item.type === 'task_assigned') {
    return taskTitle
      ? t('notifications.feed.task_assigned_body', { title: taskTitle })
      : t('notifications.feed.task_assigned_body_short');
  }

  if (item.type === 'task_changed') {
    const fromLabel = taskStatusLabel(statusFrom, t);
    const toLabel = taskStatusLabel(statusTo, t);
    const statusPart =
      fromLabel && toLabel
        ? t('notifications.feed.task_changed_status', { from: fromLabel, to: toLabel })
        : '';
    if (changeCount > 1) {
      const countPart = t('notifications.feed.task_changed_count', { count: changeCount });
      if (taskTitle) {
        return statusPart
          ? `${taskTitle} · ${countPart} · ${statusPart}`
          : `${taskTitle} · ${countPart}`;
      }
      return statusPart ? `${countPart} · ${statusPart}` : countPart;
    }
    if (taskTitle && statusPart) {
      return `${taskTitle} · ${statusPart}`;
    }
    return taskTitle || statusPart || t('notifications.feed.task_changed_body_short');
  }

  return taskTitle;
}

export function extractTaskId(item: NotificationView): string | null {
  const taskId = item.data?.taskId;
  return typeof taskId === 'string' && taskId.length > 0 ? taskId : null;
}
